import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Card, Spin, message, Button, Input, Select, Empty } from 'antd';
import { SearchOutlined, UserOutlined, CheckOutlined } from '@ant-design/icons';
import { FaUserMd } from 'react-icons/fa';
import drEasyApi from '../../../Api/Customapi';
import { getImg } from '../../../Api/getImg';

const workingDays = [
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
];

const AssignBookingToConsultantModal = ({
  visible,
  onCancel,
  onAssign,
  booking,   // expects booking.id
  isMobile,
}) => {
  const [consultants, setConsultants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterWorkingDay, setFilterWorkingDay] = useState(null);
  const [selectedConsultant, setSelectedConsultant] = useState(null);
  const [pageNumber] = useState(1);
  const [pageSize] = useState(20);
  const [totalCount, setTotalCount] = useState(0);

  const fetchConsultants = useCallback(async () => {
    setLoading(true);
    try {
      const params = { PageNumber: pageNumber, PageSize: pageSize };
      if (searchTerm.trim()) params.NameSearch = searchTerm;
      if (filterWorkingDay !== null && filterWorkingDay !== undefined)
        params.WorkingDay = filterWorkingDay;

      const response = await drEasyApi.get('/api/Centers/getMyDoctors', { params });
      setConsultants(response.data?.doctors || []);
      setTotalCount(response.data?.totalCount || 0);
    } catch (error) {
      console.error('Error fetching consultants:', error);
      message.error('Failed to fetch consultants');
      setConsultants([]);
    } finally {
      setLoading(false);
    }
  }, [pageNumber, pageSize, searchTerm, filterWorkingDay]);

  useEffect(() => {
    if (visible) {
      setSearchTerm('');
      setFilterWorkingDay(null);
      setSelectedConsultant(null);
      fetchConsultants();
    }
  }, [visible]);

  useEffect(() => {
    if (visible) fetchConsultants();
  }, [searchTerm, filterWorkingDay]);

  const handleAssign = async () => {
    if (!selectedConsultant) {
      message.warning('Please select a consultant');
      return;
    }
    setAssigning(true);
    try {
      await drEasyApi.put('/api/Book/AssignExistBookingToDoctor', {
        bookingId: booking?.id,
        doctorId: selectedConsultant.doctorId,
      });
      message.success(`Booking assigned to Dr. ${selectedConsultant.name} successfully!`);
      onAssign(selectedConsultant);
      handleCancel();
    } catch (error) {
      console.error('Error assigning consultant:', error);
      message.error(
        error.response?.data?.[0] || 'Failed to assign consultant. Please try again.'
      );
    } finally {
      setAssigning(false);
    }
  };

  const handleCancel = () => {
    setSelectedConsultant(null);
    setSearchTerm('');
    setFilterWorkingDay(null);
    onCancel();
  };

  return (
    <Modal
      title={
        <div className="flex items-center gap-3">
          <div
            className={`${
              isMobile ? 'w-6 h-6' : 'w-8 h-8'
            } bg-primary rounded-full flex items-center justify-center flex-shrink-0`}
          >
            <FaUserMd className={`text-white ${isMobile ? 'text-xs' : 'text-sm'}`} />
          </div>
          <span className={isMobile ? 'text-sm' : ''}>Assign Booking to Consultant</span>
        </div>
      }
      open={visible}
      onCancel={handleCancel}
      width={isMobile ? '95vw' : 560}
      centered={isMobile}
      footer={[
        <Button key="cancel" onClick={handleCancel}>
          Cancel
        </Button>,
        <Button
          key="assign"
          type="primary"
          onClick={handleAssign}
          loading={assigning}
          disabled={!selectedConsultant}
          style={{
            backgroundColor: selectedConsultant ? '#298d9f' : undefined,
            borderColor: selectedConsultant ? '#298d9f' : undefined,
          }}
        >
          Assign Consultant
        </Button>,
      ]}
    >
      <Spin spinning={loading}>
        {/* Filters */}
        <div className="space-y-3 mb-4">
          <Input
            placeholder="🔍 Search by consultant name"
            prefix={<SearchOutlined className="text-gray-400" />}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            allowClear
            size={isMobile ? 'middle' : 'large'}
            className="rounded-xl"
          />
          <Select
            placeholder=" Filter by working day"
            allowClear
            value={filterWorkingDay}
            onChange={(val) => setFilterWorkingDay(val ?? null)}
            size={isMobile ? 'middle' : 'large'}
            className="w-full rounded-xl"
          >
            {workingDays.map((day) => (
              <Select.Option key={day.value} value={day.value}>
                {day.label}
              </Select.Option>
            ))}
          </Select>
        </div>

        {/* Count */}
        <div className={`font-semibold text-gray-700 mb-3 ${isMobile ? 'text-sm' : 'text-base'}`}>
          📋 Select Consultant{totalCount > 0 ? ` (${totalCount} found)` : ''}
        </div>

        {/* List */}
        {consultants.length > 0 ? (
          <div className={`${isMobile ? 'max-h-64' : 'max-h-80'} overflow-y-auto space-y-2 pr-1`}>
            {consultants.map((consultant) => {
              const isSelected = selectedConsultant?.doctorId === consultant.doctorId;
              return (
                <Card
                  key={consultant.doctorId}
                  size="small"
                  className={`cursor-pointer rounded-lg transition-all duration-200 border-2 ${
                    isSelected
                      ? 'border-primary bg-blue-50 shadow-md'
                      : 'border-gray-200 hover:border-blue-300 hover:shadow-sm'
                  }`}
                  onClick={() =>
                    setSelectedConsultant(isSelected ? null : consultant)
                  }
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      {/* Avatar */}
                      <div
                        className={`${
                          isMobile ? 'w-9 h-9' : 'w-11 h-11'
                        } bg-gradient-to-r from-primary to-primary_hover rounded-full flex items-center justify-center text-white font-bold flex-shrink-0 overflow-hidden`}
                      >
                        {consultant.photoId ? (
                          <img
                            src={getImg(consultant.photoId)}
                            alt={consultant.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <UserOutlined />
                        )}
                      </div>

                      {/* Info */}
                      <div className="min-w-0">
                        <div
                          className={`font-semibold text-gray-800 truncate ${
                            isMobile ? 'text-sm' : 'text-base'
                          }`}
                        >
                          Dr. {consultant.name}
                        </div>
                        {consultant.specialization && (
                          <div
                            className={`text-gray-500 truncate ${
                              isMobile ? 'text-xs' : 'text-sm'
                            }`}
                          >
                            {consultant.specialization}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Check */}
                    {isSelected && (
                      <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center flex-shrink-0 ml-2">
                        <CheckOutlined className="text-white text-xs" />
                      </div>
                    )}
                  </div>
                </Card>
              );
            })}
          </div>
        ) : (
          !loading && (
            <Empty
              description="No consultants found"
              className="py-8"
            />
          )
        )}

        {/* Selected Summary */}
        {selectedConsultant && (
          <div className={`mt-4 p-3 bg-green-50 border border-green-200 rounded-lg ${isMobile ? 'text-xs' : 'text-sm'}`}>
            <div className="font-semibold text-green-800 mb-1">✅ Selected</div>
            <div className="text-green-700">
              Dr. {selectedConsultant.name}
              {selectedConsultant.specialization && (
                <span className="text-green-600"> — {selectedConsultant.specialization}</span>
              )}
            </div>
          </div>
        )}
      </Spin>
    </Modal>
  );
};

export default AssignBookingToConsultantModal;